import React, { useState } from 'react';
import axios from 'axios';
import { Smartphone } from 'lucide-react';
import { useToast } from '@/context/ToastContext';
import { VERIFICATION_TYPES, verificationStatus } from '@/utils/verifications';
import { DEFAULT_PHONE_COUNTRY, PHONE_COUNTRIES } from '@/utils/phoneCountries';
import { BACKEND_URL } from '@/config';

const API = BACKEND_URL + '/api';
const CHANNELS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms', label: 'SMS' },
];
const row = { marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' };

/**
 * Carte « Numéro de téléphone » de « Mes vérifications » (étape 2, 2026-10-18). Code par WhatsApp (SMS si WhatsApp
 * échoue) ou par SMS ; le serveur contrôle le numéro, le met au format international et refuse un numéro déjà vérifié
 * sur un autre compte. Vérification pas encore ouverte (`types.phone` absent) : « Bientôt disponible ».
 */
export default function PhoneCard({ user, onVerified, badge }) {
  const toast = useToast();
  const open = user?.verifications?.types?.phone !== undefined;
  const state = verificationStatus(user, 'phone');
  const verified = state === 'VERIFIED';
  const [editing, setEditing] = useState(!verified);
  const [country, setCountry] = useState(user?.phone_country || DEFAULT_PHONE_COUNTRY);
  const [phone, setPhone] = useState(verified ? '' : (user?.phone || ''));
  const [channel, setChannel] = useState('whatsapp');
  const [sent, setSent] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState('');

  const send = async (via) => {
    setBusy('send');
    try {
      const { data } = await axios.post(`${API}/verifications/phone/send`, { phone: phone.trim(), country, channel: via });
      setSent(data);
      setCode('');
      toast.success(data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Le code n’a pas pu être envoyé. Réessayez.');
    } finally { setBusy(''); }
  };

  const confirm = async (e) => {
    e.preventDefault();
    setBusy('confirm');
    try {
      await axios.post(`${API}/verifications/phone/confirm`, { code: code.trim() });
      toast.success('Numéro de téléphone vérifié.');
      setSent(null);
      setCode('');
      setPhone('');
      setEditing(false);
      await onVerified();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code incorrect.');
    } finally { setBusy(''); }
  };

  const sub = !open ? 'Bientôt disponible : un code par WhatsApp ou SMS.'
    : verified ? user?.phone_e164 : 'Un code par WhatsApp, ou par SMS si vous préférez.';

  return (
    <div className="card" style={{ marginBottom: 16 }} data-testid="verif-phone">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Smartphone size={20} aria-hidden="true" />
          <div>
            <div className="section-title" style={{ margin: 0 }}>{VERIFICATION_TYPES.phone.label}</div>
            <div style={{ fontSize: 13, color: 'var(--ink2)' }} data-testid="verif-phone-sub">{sub}</div>
          </div>
        </div>
        {open && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {badge}
            {verified && !editing && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(true)} data-testid="verif-phone-change">
                Changer de numéro
              </button>
            )}
          </div>
        )}
      </div>

      {open && editing && !sent && (
        <form onSubmit={(e) => { e.preventDefault(); send(channel); }} style={row}>
          <label htmlFor="verif-phone-country" style={{ fontSize: 13, width: '100%' }}>
            {verified ? 'Nouveau numéro — l’actuel reste vérifié jusqu’à la confirmation du nouveau.' : 'Pays et numéro de téléphone mobile.'}
          </label>
          <select id="verif-phone-country" className="field-input" value={country} onChange={(e) => setCountry(e.target.value)}
            style={{ maxWidth: 220 }} data-testid="verif-phone-country">
            {PHONE_COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name} ({c.dial})</option>)}
          </select>
          <input type="tel" className="field-input" autoComplete="tel-national" aria-label="Numéro de téléphone"
            value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="06 12 34 56 78"
            style={{ maxWidth: 200 }} data-testid="verif-phone-number" />
          <div role="radiogroup" aria-label="Recevoir le code par" style={{ display: 'flex', gap: 12, fontSize: 13, width: '100%' }}>
            {CHANNELS.map((c) => (
              <label key={c.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input type="radio" name="verif-phone-channel" value={c.value} checked={channel === c.value}
                  onChange={() => setChannel(c.value)} data-testid={`verif-phone-channel-${c.value}`} />
                {c.label}
              </label>
            ))}
          </div>
          <button type="submit" className="btn btn-primary btn-sm" disabled={phone.trim().length < 4 || busy === 'send'} data-testid="verif-phone-send">
            Recevoir le code
          </button>
          {verified && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>Annuler</button>
          )}
        </form>
      )}

      {open && sent && (
        <form onSubmit={confirm} style={row}>
          <label htmlFor="verif-phone-code" style={{ fontSize: 13, width: '100%' }}>
            Saisissez le code à 6 chiffres reçu par {sent.channel === 'sms' ? 'SMS' : 'WhatsApp'} (valable 10 minutes).
          </label>
          <input id="verif-phone-code" className="field-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="123456"
            style={{ maxWidth: 140, letterSpacing: 4 }} data-testid="verif-phone-code" />
          <button type="submit" className="btn btn-primary btn-sm" disabled={code.length !== 6 || busy === 'confirm'} data-testid="verif-phone-confirm">
            Vérifier
          </button>
          {sent.channel === 'whatsapp' && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => send('sms')} disabled={busy === 'send'} data-testid="verif-phone-sms">
              Pas reçu ? Recevoir par SMS
            </button>
          )}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSent(null)} data-testid="verif-phone-edit">
            Modifier le numéro
          </button>
        </form>
      )}
    </div>
  );
}
